import { createI18n } from 'vue-i18n'
import type { Ref } from 'vue'
import en from './locales/en.json'

/**
 * Streama CMS · i18n
 *
 * UI Sprint 1 ships English only. UI Sprint 2 adds:
 *   - locales/ar.json
 *   - RTL layout direction switching
 *   - locale-aware date/number formatting
 *
 * The discipline is set here: every user-facing string MUST come
 * from a translation key, never inline. Stylelint cannot enforce
 * this for templates, so it's a code-review rule.
 *
 * Locale persistence: the chosen locale is stored in localStorage
 * under `streama-cms.locale`. Default is 'en'. The Locale type is
 * widened to `'en' | 'ar'` even in Sprint 1 so setLocale() type-
 * checks against the eventual Sprint 2 surface — that way the
 * Arabic locale slot drops in without touching call sites.
 */

const STORAGE_KEY = 'streama-cms.locale'

export type Locale = 'en' | 'ar'

function loadInitialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'en' || stored === 'ar') return stored
  // Browser preference — Arabic if the user's first language is Arabic,
  // otherwise English. Sprint 2 will honour `ar` properly; for now
  // Arabic falls back to English silently.
  const browser = (navigator.language || 'en').toLowerCase()
  if (browser.startsWith('ar')) return 'en' // TODO Sprint 2: 'ar'
  return 'en'
}

/**
 * The vue-i18n createI18n() call infers the Locale union from whatever
 * keys are present in `messages`. To avoid widening errors when Sprint
 * 2 adds 'ar' (and to make setLocale() type-check now), we cast at the
 * boundary.
 *
 * Arabic falls back to English until ar.json ships — `fallbackLocale`
 * handles that gracefully.
 */
export const i18n = createI18n<[typeof en], Locale>({
  legacy: false,
  globalInjection: true,
  locale: loadInitialLocale(),
  fallbackLocale: 'en',
  messages: {
    en,
    // ar is intentionally omitted in Sprint 1 — fallbackLocale resolves
    // any 'ar' lookups to en.json keys until ar.json lands.
  } as Record<Locale, typeof en>,
  // Suppress missing-key warnings in dev so the console isn't
  // overwhelmed when adding screens; we'll re-enable in Sprint 3.
  missingWarn: false,
  fallbackWarn: false,
})

export function setLocale(locale: Locale): void {
  ;(i18n.global.locale as unknown as Ref<Locale>).value = locale
  localStorage.setItem(STORAGE_KEY, locale)
  // Sprint 2: also flip <html dir="rtl"> for Arabic.
  document.documentElement.lang = locale
}
